import { Show } from 'solid-js'
import bgStars from '~/assets/bg-stars.png'
import { DailyProgressBar } from '~/components/daily-progress-bar'
import { GiftBox } from '~/components/gift-box'
import { GotoIcon } from '~/components/icons/goto'
import { cn } from '~/lib/utils'
import { m } from '~/paraglide/messages'

type Props = {
  title: string
  current: number
  total: number
  chestLevel: 'lv1' | 'lv2' | 'lv3'
  chestState: 'locked' | 'ready' | 'claimed'
  loading?: boolean
  claiming?: boolean
  showArrow?: boolean
  // A Plus challenge — claiming its reward requires a subscription. Always
  // badged "PLUS Bonus" regardless of the viewer's subscription state.
  plus?: boolean
  onArrowClick?: (event: MouseEvent) => void
  // Tap on the card body (anything that isn't the arrow or a ready chest). For
  // a Plus card this is the paywall entry for non-subscribers; inert otherwise.
  onCardTap?: (event: MouseEvent) => void
  onClaimChest: (event: MouseEvent) => void
}

function resolveBarState(
  current: number,
  total: number,
  chestState: Props['chestState'],
  loading: boolean,
): 'empty' | 'partial' | 'full' | 'complete' | 'loading' {
  if (loading) return 'loading'
  const filled = total > 0 && current >= total
  if (filled) return chestState === 'claimed' ? 'complete' : 'full'
  return current <= 0 ? 'empty' : 'partial'
}

export function ChallengeCard(props: Props) {
  const ready = () => !props.loading && props.chestState === 'ready'
  const bgClass = () => (ready() ? 'bg-card-yellow' : 'bg-card-blue')

  return (
    <div
      class={cn('relative w-full', props.plus && 'pt-[22px]')}
      onClick={(e) => props.onCardTap?.(e)}
    >
      <Show when={props.plus}>
        <span
          class="absolute -top-px left-0 z-30 rounded-t-[16px] px-6 py-[4px] text-xs font-semibold leading-[16px] text-white"
          style={{
            background:
              'linear-gradient(156deg, rgba(98,240,98,0.89) 7.3%, rgb(1,177,225) 88.8%)',
            'box-shadow': 'inset 0px -3px 7.7px 0px rgba(255,255,12,0.41)',
          }}
        >
          {m.plus_bonus_tag()}
        </span>
      </Show>
      <div
        class={cn(
          'relative isolate flex w-full flex-col gap-[4px] overflow-hidden rounded-[16px] px-[16px] py-[12px] transition-colors duration-300',
          // Square the top-left so the card edge flows straight into the
          // PLUS Bonus tab above it.
          props.plus && 'rounded-tl-none',
          bgClass(),
        )}
        data-state={props.chestState}
      >
        <Show when={props.plus}>
          <div
            aria-hidden="true"
            class="pointer-events-none absolute inset-0 z-20 animate-gradient-spin rounded-[16px] rounded-tl-none"
            style={{
              padding: '1.2px',
              background:
                'conic-gradient(from var(--gradient-angle), #44E9FF 0%, #2FC788 38%, rgba(98,240,98,0.89) 38%, rgba(98,240,98,0.89) 100%)',
              '-webkit-mask':
                'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
              '-webkit-mask-composite': 'xor',
              'mask-composite': 'exclude',
            }}
          />
        </Show>
        <Show when={ready()}>
          <img
            src={bgStars}
            alt=""
            aria-hidden="true"
            draggable={false}
            class="pointer-events-none absolute left-[-5px] right-[-5px] top-1/2 h-[134px] w-[calc(100%+10px)] max-w-none -translate-y-1/2 object-cover object-center"
          />
        </Show>

        <div
          class="pointer-events-none absolute inset-0 overflow-hidden"
          aria-hidden="true"
        >
          <div class="absolute -bottom-[60%] -top-[60%] right-[86px] w-[66px] rotate-[31deg] skew-x-[3deg] bg-gradient-to-b from-white/0 via-white/25 to-white opacity-60" />
          <div class="absolute -bottom-[60%] -top-[60%] right-[20px] w-[26px] rotate-[31deg] skew-x-[3deg] bg-gradient-to-b from-white/0 via-white/20 to-white opacity-60" />
        </div>

        <Show when={ready()}>
          <div
            class="pointer-events-none absolute inset-0 overflow-hidden"
            aria-hidden="true"
          >
            <div class="absolute -bottom-[60%] -left-[40%] -top-[60%] flex h-[220%] w-[60%] animate-challenge-sweep items-center justify-center">
              <div class="h-full w-[34%] rotate-[31deg] skew-x-[3deg] bg-gradient-to-b from-white/0 via-white/55 to-white opacity-90" />
              <div class="ml-[10%] h-full w-[14%] rotate-[31deg] skew-x-[3deg] bg-gradient-to-b from-white/0 via-white/45 to-white opacity-80" />
            </div>
          </div>
        </Show>

        <div class="relative flex w-full items-center gap-[12px]">
          <div class="flex min-h-[48px] flex-1 items-center">
            <Show
              when={!props.loading}
              fallback={
                <div class="flex w-full flex-col gap-[6px]">
                  <div class="h-[14px] w-[70%] animate-pulse rounded-[4px] bg-black/10" />
                  <div class="h-[14px] w-[45%] animate-pulse rounded-[4px] bg-black/10" />
                </div>
              }
            >
              <p class="line-clamp-2 text-[16px] font-semibold leading-[22px] text-ink">
                {props.title}
              </p>
            </Show>
          </div>
          <Show when={props.showArrow && !props.loading}>
            <button
              type="button"
              class="shrink-0 appearance-none rounded-full border-0 bg-transparent p-0"
              onClick={(e) => {
                // Keep the deeplink as the lone card exception: don't let the
                // tap bubble to the card body's paywall handler.
                e.stopPropagation()
                props.onArrowClick?.(e)
              }}
            >
              <GotoIcon class="h-[32px] w-[32px]" />
            </button>
          </Show>
        </div>

        <div class="relative flex w-full items-end pr-[20px]">
          <div class="-mr-[20px] flex flex-1 flex-col items-start py-[8px]">
            <DailyProgressBar
              current={props.current}
              total={props.total}
              state={resolveBarState(
                props.current,
                props.total,
                props.chestState,
                props.loading ?? false,
              )}
            />
          </div>
          <div class="-mr-[20px]">
            <GiftBox
              level={props.chestLevel}
              state={props.chestState}
              loading={props.loading || props.claiming}
              onClick={(e) => {
                // A ready chest claims (or, for a non-subscriber, routes to the
                // paywall) on its own; stop the tap reaching the card body. A
                // non-ready chest is pointer-events-none and never gets here —
                // its taps fall through to the card body instead.
                e.stopPropagation()
                props.onClaimChest(e)
              }}
            />
          </div>
        </div>

        <Show when={ready()}>
          <svg
            aria-hidden="true"
            viewBox="0 0 14.2369 14.2369"
            class="pointer-events-none absolute right-[10px] top-[56px] z-50 block h-[14px] w-[14px] animate-sparkle"
          >
            <path
              d="M6.18061 0.652964C6.50276 -0.217655 7.73415 -0.217655 8.05631 0.652964L9.3897 4.2564C9.49099 4.53012 9.7068 4.74593 9.98052 4.84721L13.584 6.18061C14.4546 6.50276 14.4546 7.73415 13.584 8.05631L9.98052 9.3897C9.7068 9.49099 9.49099 9.7068 9.3897 9.98052L8.05631 13.584C7.73415 14.4546 6.50276 14.4546 6.18061 13.584L4.84721 9.98052C4.74593 9.7068 4.53012 9.49099 4.2564 9.3897L0.652964 8.05631C-0.217655 7.73415 -0.217655 6.50276 0.652964 6.18061L4.2564 4.84721C4.53012 4.74593 4.74593 4.53012 4.84721 4.2564L6.18061 0.652964Z"
              fill="white"
            />
          </svg>
        </Show>
      </div>
    </div>
  )
}
